/**
 * SQA Service — wraps /api/admin/sqa/ (14 admin + 3 tenant endpoints)
 * Phase 4.7 — WQ-164
 */

export interface SQAComponent {
  id: string;
  name: string;
  slug: string;
  integrity_level: string;
  description: string | null;
  mechanism: string | null;
  safeguards: Record<string, unknown> | null;
  source_files: Record<string, unknown> | null;
  laws_relevance: Record<string, unknown> | null;
  introduced_in_phase: string | null;
  introduced_in_wq: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SQAAssessment {
  id: string;
  component_id: string;
  assessed_by: string;
  wq_id: string | null;
  status: string;
  confidence_score: number;
  test_coverage_score: number;
  conformance_score: number;
  laws_compliance_score: number;
  security_score: number;
  audit_trail_score: number;
  evidence: Record<string, unknown> | null;
  notes: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NCR {
  id: string;
  component_id: string;
  assessment_id: string | null;
  title: string;
  description: string;
  severity: string;
  status: string;
  raised_by: string;
  resolution: string | null;
  resolved_by: string | null;
  resolution_wq: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformSummary {
  total_components: number;
  by_integrity_level: Record<string, number>;
  overall_confidence: number;
  open_ncrs: number;
  recent_assessments: number;
  threshold_violation_count: number;
  last_assessment_date: string | null;
}

export interface ReportCard {
  component: SQAComponent;
  latest_assessment: SQAAssessment | null;
  assessment_count: number;
  open_ncrs: number;
  confidence_meets_threshold: boolean;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// --- Components ---

export async function listComponents(
  authFetch: FetchFn,
  integrityLevel?: string
): Promise<SQAComponent[]> {
  const qs = integrityLevel ? `?integrity_level=${integrityLevel}` : '';
  const res = await authFetch(`/api/admin/sqa/components${qs}`);
  if (!res.ok) throw new Error(`Failed to list components: ${res.status}`);
  return res.json();
}

export async function getComponent(authFetch: FetchFn, id: string): Promise<SQAComponent> {
  const res = await authFetch(`/api/admin/sqa/components/${id}`);
  if (!res.ok) throw new Error(`Failed to get component: ${res.status}`);
  return res.json();
}

export async function createComponent(
  authFetch: FetchFn,
  data: {
    name: string; slug: string; integrity_level: string;
    description?: string; mechanism?: string;
  }
): Promise<SQAComponent> {
  const res = await authFetch('/api/admin/sqa/components', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create component: ${res.status}`);
  return res.json();
}

export async function updateComponent(
  authFetch: FetchFn,
  id: string,
  data: Partial<SQAComponent>
): Promise<SQAComponent> {
  const res = await authFetch(`/api/admin/sqa/components/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update component: ${res.status}`);
  return res.json();
}

// --- Assessments ---

export async function listAssessments(
  authFetch: FetchFn,
  filters?: { component_id?: string; status?: string }
): Promise<SQAAssessment[]> {
  const params = new URLSearchParams();
  if (filters?.component_id) params.set('component_id', filters.component_id);
  if (filters?.status) params.set('status', filters.status);
  const qs = params.toString();
  const res = await authFetch(`/api/admin/sqa/assessments${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list assessments: ${res.status}`);
  return res.json();
}

export async function getAssessment(authFetch: FetchFn, id: string): Promise<SQAAssessment> {
  const res = await authFetch(`/api/admin/sqa/assessments/${id}`);
  if (!res.ok) throw new Error(`Failed to get assessment: ${res.status}`);
  return res.json();
}

export async function createAssessment(
  authFetch: FetchFn,
  data: {
    component_id: string; assessed_by: string; wq_id?: string;
    test_coverage_score: number; conformance_score: number;
    laws_compliance_score: number; security_score: number;
    audit_trail_score: number; evidence?: Record<string, unknown>;
    notes?: string;
  }
): Promise<{ assessment: SQAAssessment; warnings: string[] }> {
  const res = await authFetch('/api/admin/sqa/assessments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create assessment: ${res.status}`);
  return res.json();
}

export async function getLatestAssessment(
  authFetch: FetchFn,
  componentId: string
): Promise<SQAAssessment> {
  const res = await authFetch(`/api/admin/sqa/assessments/latest/${componentId}`);
  if (!res.ok) throw new Error(`Failed to get latest assessment: ${res.status}`);
  return res.json();
}

// --- NCRs ---

export async function listNCRs(
  authFetch: FetchFn,
  filters?: { component_id?: string; status?: string; severity?: string }
): Promise<NCR[]> {
  const params = new URLSearchParams();
  if (filters?.component_id) params.set('component_id', filters.component_id);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.severity) params.set('severity', filters.severity);
  const qs = params.toString();
  const res = await authFetch(`/api/admin/sqa/ncrs${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list NCRs: ${res.status}`);
  return res.json();
}

export async function createNCR(
  authFetch: FetchFn,
  data: {
    component_id: string; title: string; description: string;
    severity: string; assessment_id?: string;
  }
): Promise<NCR> {
  const res = await authFetch('/api/admin/sqa/ncrs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create NCR: ${res.status}`);
  return res.json();
}

export async function updateNCR(
  authFetch: FetchFn,
  id: string,
  data: { status: string; resolution?: string; resolved_by?: string; resolution_wq?: string }
): Promise<NCR> {
  const res = await authFetch(`/api/admin/sqa/ncrs/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update NCR: ${res.status}`);
  return res.json();
}

// --- Summary & Report Card ---

export async function getPlatformSummary(authFetch: FetchFn): Promise<PlatformSummary> {
  const res = await authFetch('/api/admin/sqa/summary');
  if (!res.ok) throw new Error(`Failed to get SQA summary: ${res.status}`);
  return res.json();
}

export async function getReportCard(authFetch: FetchFn, componentId: string): Promise<ReportCard> {
  const res = await authFetch(`/api/admin/sqa/report-card/${componentId}`);
  if (!res.ok) throw new Error(`Failed to get report card: ${res.status}`);
  return res.json();
}
